// A predefined list of Tailwind color names that work well for badges.
// We exclude 'slate', 'gray', 'zinc', 'neutral', 'stone' to avoid looking like disabled states.
const TAILWIND_COLORS = [
  "red",
  "orange",
  "amber",
  "yellow",
  "lime",
  "green",
  "emerald",
  "teal",
  "cyan",
  "sky",
  "blue",
  "indigo",
  "violet",
  "purple",
  "fuchsia",
  "pink",
  "rose",
];

/**
 * Generates a consistent hash code from a string.
 * Uses a variant of the djb2 algorithm which provides good distribution
 * to avoid collisions for similar strings.
 */
const getHashCode = (str: string): number => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
};

/**
 * Returns a string of Tailwind CSS classes based on the input string.
 * * Strategy:
 * 1. Light Mode: Very light background (50), dark text (700), subtle border (600/10).
 * 2. Dark Mode: Dark transparent background (400/10), light text (400), subtle border (400/30).
 * 3. Ring: Used to create an inset border which adds definition.
 */
export const stringToBadgeClass = (str: string): string => {
  if (!str)
    return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"; // Fallback

  const hash = getHashCode(str);
  const colorIndex = hash % TAILWIND_COLORS.length;
  const color = TAILWIND_COLORS[colorIndex];

  // Specific handling for yellow/lime/amber to ensure contrast is high enough
  const isLightColor = ["yellow", "lime", "amber"].includes(color);

  const textShade = isLightColor ? "800" : "700";
  const darkTextShade = isLightColor ? "300" : "400";

  return `
    inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset
    bg-${color}-50 text-${color}-${textShade} ring-${color}-600/20 
    dark:bg-${color}-400/10 dark:text-${color}-${darkTextShade} dark:ring-${color}-400/30
  `
    .replace(/\s+/g, " ")
    .trim(); // Cleanup newlines
};
